import React, { FunctionComponent } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Tooltip,
  Autocomplete,
  TextField,
  Popper,
  InputAdornment,
} from "@mui/material";
import { EsgArrowDown } from "assets";
import { COLORS } from "components";

import { EsgSingleSelectControlledFieldProps } from "./types";

export const EsgSingleSelectControlledField: FunctionComponent<
  EsgSingleSelectControlledFieldProps
> = ({
  label,
  disabled = false,
  color,
  message,
  selectedValue,
  options,
  setSelectedValue,
  placeholder = "Select an option",
  width = "200px",
  height = "36px",
  sx,
  sxLabel,
  sxPlaceholder,
  sxSelect,
  sxMenuItem,
  sxPoppover,
  allowDeselect = false,
  required = false,
  disableFiltering = false,
  accentColor,
  valueFontSize,
}) => {
  const resolvedAccentColor = accentColor ?? COLORS.Mainblue;
  const selectedValueString = Array.isArray(selectedValue)
    ? selectedValue.toString()
    : selectedValue;
  const selectedOption =
    options.find(
      (option) =>
        option.value === selectedValueString ||
        option.label === selectedValueString,
    ) || null;

  return (
    <Box
      data-testid="esg-single-select-container"
      sx={{
        width,
        "& > div:first-of-type": {
          height,
        },
        ...sx,
      }}
    >
      {label && (
        <InputLabel
          sx={{
            fontSize: "12px",
            color: "black",
            fontWeight: 600,
            marginBottom: "4px",
            ...sxLabel,
          }}
        >
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </InputLabel>
      )}
      <FormControl fullWidth>
        <Autocomplete
          disablePortal={false}
          options={options}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) =>
            option.value === value?.value
          }
          getOptionDisabled={(option) => !!option.disabled}
          value={selectedOption}
          onChange={(event, newValue) => {
            if (!allowDeselect && !newValue) {
              return;
            }
            setSelectedValue(newValue?.value ?? "");
          }}
          disabled={disabled}
          disableClearable={!allowDeselect}
          filterOptions={disableFiltering ? (x) => x : undefined}
          popupIcon={
            <EsgArrowDown
              style={{
                fill: disabled ? COLORS.muiDisabled : resolvedAccentColor,
                opacity: disabled ? 0.6 : 1,
              }}
            />
          }
          ListboxProps={{
            sx: {
              maxHeight: "180px",
              overflowY: "auto",
            },
          }}
          PopperComponent={({ style, ...props }) => (
            <Popper
              {...props}
              style={{
                ...style,
                ...sxPoppover,
                zIndex: 1300,
              }}
              placement="bottom-start"
            />
          )}
          renderInput={({ inputProps: nativeInputProps, InputProps: muiInputProps, ...restParams }) => (
            <Box sx={{ position: "relative", cursor: "pointer" }}>
              <TextField
                {...restParams}
                value={selectedOption}
                placeholder={!selectedOption ? placeholder : ""}
                InputProps={{
                  ...muiInputProps,
                  readOnly: disableFiltering,
                  inputProps: {
                    ...(nativeInputProps as object),
                    readOnly: disableFiltering,
                  },
                  startAdornment: selectedOption?.icon ? (
                    <>
                      <InputAdornment
                        position="start"
                        sx={{ ml: "2px", mr: 0 }}
                      >
                        {selectedOption.icon}
                      </InputAdornment>
                      {muiInputProps.startAdornment}
                    </>
                  ) : (
                    muiInputProps.startAdornment
                  ),
                }}
                variant="outlined"
                sx={{
                  height,
                  "& .MuiOutlinedInput-root": {
                    height,
                    borderRadius: "8px",
                    fontWeight: 500,
                    color: resolvedAccentColor,
                    "& fieldset": {
                      borderColor: resolvedAccentColor,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: resolvedAccentColor,
                      borderWidth: "1px",
                    },
                    "& svg": {
                      margin: "5px 3px",
                      "&.Mui-disabled *": {
                        fill: COLORS.muiDisabled,
                      },
                    },
                    "&.Mui-disabled": {
                      backgroundColor: COLORS.disabled,
                      WebkitTextFillColor: COLORS.muiDisabled,
                    },
                  },
                  "& .MuiInputBase-input": {
                    cursor: "pointer",
                    ...(valueFontSize && { fontSize: valueFontSize }),
                    ...(selectedOption?.selectedElement && {
                      color: "transparent",
                      caretColor: "transparent",
                    }),
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "black",
                    margin: 0,
                    opacity: 0.4,
                    fontSize: "12px",
                    fontWeight: "500",
                    fontStyle: "italic",
                    ...sxPlaceholder,
                  },
                  ...sxSelect,
                }}
              />
              {selectedOption?.selectedElement && (
                <Box
                  sx={{
                    position: "absolute",
                    height: "100%",
                    left: 14,
                    right: 40,
                    top: 0,
                    bottom: 0,
                    display: "flex",
                    py: "2px",
                    alignItems: "center",
                    pointerEvents: "none",
                    cursor: "pointer",
                    overflow: "hidden",
                  }}
                >
                  {selectedOption.selectedElement}
                </Box>
              )}
            </Box>
          )}
          renderOption={(props, option) => {
            const optionContent = (
              <Box
                component="li"
                {...props}
                key={option.value as string | number}
                sx={{
                  color: option.disabled ? COLORS.muiDisabled : COLORS.Mainblue,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  justifyContent: "flex-start",
                  "&:hover": {
                    backgroundColor: option.disabled
                      ? "transparent"
                      : COLORS.White_Lilac,
                  },
                  "&.Mui-selected": {
                    backgroundColor: option.disabled
                      ? "transparent"
                      : COLORS.White_Lilac,
                  },
                  "&.Mui-disabled": {
                    opacity: 0.6,
                  },
                  ...sxMenuItem,
                }}
              >
                {option.labelElement ?? (
                  <>
                    {option.icon && option.icon}
                    {option.label}
                  </>
                )}
              </Box>
            );

            if (option.disabled && option.tooltip) {
              return (
                <Tooltip
                  key={option.value as string | number}
                  title={option.tooltip}
                  placement="top"
                  arrow
                >
                  {optionContent}
                </Tooltip>
              );
            }

            return optionContent;
          }}
        />
      </FormControl>
      {message && (
        <Box
          sx={{
            fontSize: "12px",
            color: color === "error" ? COLORS.red : COLORS.Mainblue,
            marginTop: "4px",
          }}
        >
          {message}
        </Box>
      )}
    </Box>
  );
};
